import { useCallback, useRef, useState } from 'react';
import { useDetectElementChange } from '../../../hooks/useDetectElementChange';

interface Props {
    isHintedEllipsis: boolean;
    ref: React.ForwardedRef<HTMLSpanElement>;
}

export const useHintedOverflowText = ({ isHintedEllipsis, ref }: Props) => {
    const internalRef = useRef<HTMLElement>(null);

    const [isOverflowing, setIsOverflowing] = useState(false);

    const combineRefs = useCallback(
        (node: HTMLSpanElement | null) => {
            internalRef.current = node;

            if (node && isHintedEllipsis) {
                const isOverflowingHorizontally =
                    node.scrollWidth > node.clientWidth;
                const isOverflowingVertically =
                    node.scrollHeight > node.clientHeight;
                setIsOverflowing(
                    isOverflowingHorizontally || isOverflowingVertically
                );
            }

            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                ref.current = node;
            }
        },
        [ref, isHintedEllipsis]
    );

    const detectTextOverflow = useCallback(() => {
        const element = internalRef.current;
        if (!element) {
            return;
        }
        const isOverflowingHorizontally =
            element.scrollWidth > element.clientWidth;
        const isOverflowingVertically =
            element.scrollHeight > element.clientHeight;
        setIsOverflowing(isOverflowingHorizontally || isOverflowingVertically);
    }, []);

    useDetectElementChange({
        element: internalRef.current,
        callback: isHintedEllipsis ? detectTextOverflow : null
    });

    return { combineRefs, isOverflowing };
};
